import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Upload, X, Calendar, AlertTriangle, FileText, Save } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QCIncidentForm = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const isAdmin = user?.roles?.includes('Admin') || user?.roles?.includes('profile_5');
  
  const [formData, setFormData] = useState({
    photos: [],
    incident_date: new Date().toISOString().split('T')[0],
    project_name: '',
    project_id: '',
    supplier_id: '',
    supplier_name: '',
    assigned_to: user?.id || '', // Default to current user
    assigned_to_name: user?.name || '',
    description: '',
    failure_points: [],
    importance: 'Medium'
  });
  
  const [newFailurePoint, setNewFailurePoint] = useState('');

  useEffect(() => {
    fetchSuppliers();
    fetchEmployees();
    fetchProjects();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users?rows_per_page=1000`, { headers });
      // Filter for suppliers (profile_29 or profile_34)
      const allUsers = response.data.records || [];
      console.log('All users for suppliers:', allUsers.length);
      const supplierUsers = allUsers.filter(u => {
        // profile_keys_raw is the array, profile_keys is a string
        const keysArray = u.profile_keys_raw || [];
        const isSupplier = keysArray.some(k => k.id === 'profile_29' || k.id === 'profile_34');
        return isSupplier;
      });
      console.log('Suppliers filtered:', supplierUsers.length);
      setSuppliers(supplierUsers);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users?rows_per_page=1000`, { headers });
      // Filter for employees (not suppliers)
      const allUsers = response.data.records || [];
      console.log('All users fetched:', allUsers.length);
      const employeeUsers = allUsers.filter(u => {
        // profile_keys_raw is the array, profile_keys is a string
        const keysArray = u.profile_keys_raw || [];
        const isSupplier = keysArray.some(k => k.id === 'profile_29' || k.id === 'profile_34');
        return !isSupplier;
      });
      console.log('Employees filtered:', employeeUsers.length);
      setEmployees(employeeUsers);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/projects?rows_per_page=1000`, { headers });
      setProjects(response.data.records || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/qc-incidents/upload-image`, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        return response.data.url;
      });

      const urls = await Promise.all(uploadPromises);
      
      // Update photos array without recreating the entire form data
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...urls]
      }));
      
      console.log('Photos uploaded successfully');
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload images');
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };
  
  const addFailurePoint = () => {
    if (newFailurePoint.trim()) {
      setFormData(prev => ({
        ...prev,
        failure_points: [...prev.failure_points, newFailurePoint.trim()]
      }));
      setNewFailurePoint('');
    }
  };
  
  const removeFailurePoint = (index) => {
    setFormData(prev => ({
      ...prev,
      failure_points: prev.failure_points.filter((_, i) => i !== index)
    }));
  };

  const handleSupplierChange = (e) => {
    const supplierId = e.target.value;
    const supplier = suppliers.find(s => s.id === supplierId);
    const supplierName = supplier ? (supplier.field_12 || supplier.name) : '';
    setFormData(prev => ({
      ...prev,
      supplier_id: supplierId,
      supplier_name: supplierName
    }));
  };

  const handleEmployeeChange = (e) => {
    const employeeId = e.target.value;
    const employee = employees.find(emp => emp.id === employeeId);
    const employeeName = employee ? (employee.field_12 || employee.name) : '';
    setFormData(prev => ({
      ...prev,
      assigned_to: employeeId,
      assigned_to_name: employeeName
    }));
  };

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    const project = projects.find(p => p.id === projectId);
    setFormData(prev => ({
      ...prev,
      project_id: projectId,
      project_name: project ? (project.field_2 || '') : prev.project_name
    }));
  };

  const handleSubmit = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    
    if (!formData.supplier_id) {
      setErrorMessage('Please select a supplier');
      return;
    }

    if (!formData.description) {
      setErrorMessage('Please provide a description of the problem');
      return;
    }

    setLoading(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const incidentData = {
        photos: formData.photos,
        incident_date: formData.incident_date,
        project_name: formData.project_name,
        project_id: formData.project_id,
        supplier_id: formData.supplier_id,
        supplier_name: formData.supplier_name,
        assigned_to: formData.assigned_to,
        assigned_to_name: formData.assigned_to_name,
        description: formData.description,
        failure_points: Array.isArray(formData.failure_points) 
          ? formData.failure_points.join('\n') 
          : formData.failure_points,
        importance: formData.importance
      };
      
      console.log('Submitting incident:', incidentData);
      
      const response = await axios.post(`${API}/qc-incidents`, incidentData, { headers });
      
      console.log('Incident created:', response.data);
      setSuccessMessage('Incident registered successfully!');
      
      // Reset form
      setFormData({
        photos: [],
        incident_date: new Date().toISOString().split('T')[0],
        project_name: '',
        project_id: '',
        supplier_id: '',
        supplier_name: '',
        assigned_to: user?.id || '',
        assigned_to_name: user?.name || '',
        description: '',
        failure_points: [],
        importance: 'Medium'
      });
      setNewFailurePoint('');
      
      // Navigate to incidents list after a short delay
      setTimeout(() => {
        navigate('/qc-incidents');
      }, 1500);
      
    } catch (error) {
      console.error('Failed to register incident:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to register incident';
      setErrorMessage(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <AlertTriangle className="w-7 h-7 text-orange-600 dark:text-orange-400" />
          Register QC Incident
        </h1>
        <p className="text-gray-600 dark:text-gray-400">Document a new quality control incident</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 space-y-6">
        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
            {errorMessage}
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg">
            {successMessage}
          </div>
        )}

        {/* Photo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Photos
          </label>
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="photo-upload"
              disabled={uploading}
            />
            <label
              htmlFor="photo-upload"
              className="flex flex-col items-center justify-center cursor-pointer"
            >
              <Upload className="w-12 h-12 text-gray-400 mb-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {uploading ? 'Uploading...' : 'Click to upload photos'}
              </span>
            </label>
          </div>

          {formData.photos.length > 0 && (
            <div className="mt-4 grid grid-cols-4 gap-4">
              {formData.photos.map((photo, index) => (
                <div key={index} className="relative group">
                  <img
                    src={photo}
                    alt={`Upload ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Incident Date - Moved below photo upload */}
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Incident Date</label>
          <input
            type="date"
            value={formData.incident_date}
            onChange={(e) => setFormData(prev => ({ ...prev, incident_date: e.target.value }))}
            max={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Project Name
            </label>
            <input
              type="text"
              value={formData.project_name}
              onChange={(e) => setFormData(prev => ({ ...prev, project_name: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Project (Optional)
            </label>
            <select
              value={formData.project_id}
              onChange={handleProjectChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Select a project...</option>
              {projects.map(project => {
                const ge = project.field_83_raw?.[0]?.identifier || '';
                const projectName = project.field_2 || 'Unnamed Project';
                return (
                  <option key={project.id} value={project.id}>
                    {ge} - {projectName}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Registered By (Read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Registered By
          </label>
          <input
            type="text"
            value={user?.name || 'Current User'}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
            readOnly
          />
        </div>

        {/* Assigned To */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Assigned To *
          </label>
          {isAdmin ? (
            <select
              value={formData.assigned_to}
              onChange={handleEmployeeChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            >
              <option value="">Select an employee...</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.field_12 || employee.name || 'Unknown'}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={user?.name || 'Current User'}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
              readOnly
            />
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {isAdmin ? 'Admins can assign to any employee' : 'You can only assign incidents to yourself'}
          </p>
        </div>

        {/* Supplier */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Supplier *
          </label>
          <select
            value={formData.supplier_id}
            onChange={handleSupplierChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            required
          >
            <option value="">Select a supplier...</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.field_12 || supplier.name || 'Unknown Supplier'}
              </option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description of the Problem *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={5}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            required
          />
        </div>

        {/* Action Items */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Action Items
          </label>
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={newFailurePoint}
                onChange={(e) => setNewFailurePoint(e.target.value)}
                placeholder="Add an action item..."
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                onKeyPress={(e) => e.key === 'Enter' && addFailurePoint()}
              />
              <button
                type="button"
                onClick={addFailurePoint}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Add
              </button>
            </div>
            {formData.failure_points.length > 0 && (
              <div className="space-y-1">
                {formData.failure_points.map((point, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <span className="flex-1 text-sm">{point}</span>
                    <button
                      type="button"
                      onClick={() => removeFailurePoint(index)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Importance Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Importance Level *
          </label>
          <select
            value={formData.importance}
            onChange={(e) => setFormData(prev => ({ ...prev, importance: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            required
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => navigate('/qc-incidents')}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || uploading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Register Incident
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QCIncidentForm;
